import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, api } from '@/lib/api';
import { beginLogin, isHiveMode } from '@/lib/hive';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';

// Billing isn't finalized yet, so self-serve account creation is temporarily
// disabled: /signup collects access requests instead (POST
// /api/access-requests, viewable at /_admin/access-requests). Flip this back
// to true to restore the original signup form — kept intact below.
const ACCOUNT_CREATION_ENABLED = false;

export function SignupPage() {
  if (!ACCOUNT_CREATION_ENABLED) return <RequestAccessForm />;
  return <CreateAccountForm />;
}

function RequestAccessForm() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api('/api/access-requests', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({
          full_name: fullName,
          email,
          phone: phone.trim() || null,
        }),
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many requests — please try again in a minute.');
      } else {
        setError('Something went wrong submitting your request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-6">
        <Card className="w-full">
          <h1 className="text-2xl font-semibold">Request received</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Thanks, {fullName}! We'll reach out at <strong>{email}</strong> as soon as your
            access is ready.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-6">
      <Card className="w-full">
        <h1 className="text-2xl font-semibold">Request access to Athena</h1>
        <p className="mt-1 text-sm text-slate-500">
          We're onboarding teams in waves. Leave your details and we'll get you set up.
          Already have an account?{' '}
          <Link to="/login" className="text-[var(--color-accent)] underline-offset-4 hover:underline">
            Log in
          </Link>
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4" aria-busy={submitting}>
          <div className="space-y-1.5">
            <Label htmlFor="fullName">Full name</Label>
            <Input
              id="fullName"
              autoComplete="name"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone number (optional)</Label>
            <Input
              id="phone"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          {error && (
            <div aria-live="polite" className="text-sm text-[var(--color-critical)]">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Request access'}
          </Button>
          <p className="text-center text-xs text-slate-500">
            We'll only use your details to contact you about Athena access.
          </p>
        </form>
      </Card>
    </div>
  );
}

function CreateAccountForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [verifyNotice, setVerifyNotice] = useState(false);

  // Hive mode: /signup is a stale bookmark — accounts live in Hive now.
  useEffect(() => {
    if (isHiveMode()) void beginLogin('/app');
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/verify-email`,
      },
    });
    setSubmitting(false);

    if (signUpError) {
      setError(signUpError.message);
      return;
    }

    // Org creation runs server-side once the user has a verified email +
    // access token (Phase 1.7 wires POST /api/me/bootstrap). If Supabase
    // returned a session immediately (email-confirm disabled), jump in.
    if (data.session) {
      navigate({ to: '/app' });
    } else {
      setVerifyNotice(true);
    }
  }

  if (isHiveMode()) {
    return (
      <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-6">
        <Card className="w-full">
          <h1 className="text-2xl font-semibold">Redirecting to sign-in…</h1>
          <p className="mt-2 text-sm text-slate-500">
            Athena uses Hive single sign-on — create your account there.{' '}
            <button
              type="button"
              onClick={() => void beginLogin('/app')}
              className="text-[var(--color-accent)] underline-offset-4 hover:underline"
            >
              Continue to Hive
            </button>{' '}
            if you aren't redirected automatically.
          </p>
        </Card>
      </div>
    );
  }

  if (verifyNotice) {
    return (
      <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-6">
        <Card className="w-full">
          <h1 className="text-2xl font-semibold">Check your email</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            We sent a verification link to <strong>{email}</strong>. Click it to finish signup.
          </p>
          <p className="mt-4 text-xs text-slate-500">
            Didn't get it? Check spam, or <Link to="/login" className="underline">log in</Link> if
            you've already verified.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-6">
      <Card className="w-full">
        <h1 className="text-2xl font-semibold">Create your Athena account</h1>
        <p className="mt-1 text-sm text-slate-500">
          Already have an account?{' '}
          <Link to="/login" className="text-[var(--color-accent)] underline-offset-4 hover:underline">
            Log in
          </Link>
        </p>

        {!isSupabaseConfigured && (
          <div className="mt-4 rounded-md border border-[var(--color-warning)] bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">
            Supabase not configured. Set <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_ANON_KEY</code> in the repo-root <code>.env</code>, then restart the dev server.
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-6 space-y-4" aria-busy={submitting}>
          <div className="space-y-1.5">
            <Label htmlFor="fullName">Your name</Label>
            <Input
              id="fullName"
              autoComplete="name"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Work email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="text-xs text-slate-500">At least 8 characters.</p>
          </div>

          {error && (
            <div aria-live="polite" className="text-sm text-[var(--color-critical)]">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={submitting || !isSupabaseConfigured}>
            {submitting ? 'Creating account…' : 'Create account'}
          </Button>
          <p className="text-center text-xs text-slate-500">
            By signing up you agree to our terms and privacy policy.
          </p>
        </form>
      </Card>
    </div>
  );
}
